import { useMemo } from 'react'
import { Chip, KanbanBoard, type KanbanItem, type KanbanStage } from '@/components/ui'
import { usePreferences } from '@/shell/stores/preferences'
import type { SampleOrderSummary, Stage } from '../api/types'
import {
  BLOCKED_REASON_LABELS,
  BOARD_STAGES,
  STAGE_LABELS,
  STAGE_VARIANT,
  transitionAllowed,
} from '../utils/stage-meta'

// Stable string — don't rename without migrating existing user prefs
// (same convention as sales-inbox / sample-requests kanbanStageOrder keys).
const MODULE_ID = 'sample-orders'

interface SampleOrderKanbanItem extends KanbanItem {
  order: SampleOrderSummary
}

export interface SampleOrdersKanbanProps {
  items: SampleOrderSummary[]
  selectedId?: string
  onSelect: (order: SampleOrderSummary) => void
  onTransition: (order: SampleOrderSummary, target: Stage) => void
}

function kanbanAccent(
  v: 'neutral' | 'success' | 'warning' | 'danger' | 'info',
): 'neutral' | 'success' | 'warning' | 'danger' {
  // KanbanStage's accent doesn't have an `info` slot — fold info → neutral.
  if (v === 'info') return 'neutral'
  return v
}

const STAGE_BY_ID: Record<Stage, KanbanStage> = BOARD_STAGES.reduce(
  (acc, s) => {
    acc[s] = { id: s, label: STAGE_LABELS[s], accent: kanbanAccent(STAGE_VARIANT[s]) }
    return acc
  },
  {} as Record<Stage, KanbanStage>,
)

/** Same "user order first, canonical tail" composition as sales-inbox /
 * sample-requests — handles new stages added later gracefully. */
function composeStageOrder(userOrder: ReadonlyArray<string>): KanbanStage[] {
  const seen = new Set<string>()
  const out: KanbanStage[] = []
  for (const id of userOrder) {
    if (id in STAGE_BY_ID && !seen.has(id)) {
      out.push(STAGE_BY_ID[id as Stage])
      seen.add(id)
    }
  }
  for (const s of BOARD_STAGES) {
    if (!seen.has(s)) {
      out.push(STAGE_BY_ID[s])
      seen.add(s)
    }
  }
  return out
}

export function SampleOrdersKanban({
  items,
  selectedId,
  onSelect,
  onTransition,
}: SampleOrdersKanbanProps) {
  const userOrder = usePreferences((s) => s.kanbanStageOrder[MODULE_ID]) ?? []
  const setKanbanStageOrder = usePreferences((s) => s.setKanbanStageOrder)

  const stages = useMemo(() => composeStageOrder(userOrder), [userOrder])

  const kanbanItems: SampleOrderKanbanItem[] = useMemo(
    () =>
      items.map((order) => ({
        id: order.id,
        stageId: order.status,
        order,
      })),
    [items],
  )

  return (
    <KanbanBoard<SampleOrderKanbanItem>
      stages={stages}
      items={kanbanItems}
      selectedId={selectedId}
      allowedTransitions={(from, to) => transitionAllowed(from as Stage, to as Stage)}
      onAdvance={(item, _from, to) => onTransition(item.order, to as Stage)}
      onSelect={(item) => onSelect(item.order)}
      onStageReorder={(next) => setKanbanStageOrder(MODULE_ID, next)}
      renderCard={(item) => <SampleOrderKanbanCard order={item.order} />}
      scrollRestorationKey="sample-orders"
    />
  )
}

function SampleOrderKanbanCard({ order }: { order: SampleOrderSummary }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-role-data-primary truncate">{order.order_number}</div>
      <div className="text-role-data-secondary truncate">
        {order.customer_name ?? order.prospect_name ?? (
          <em className="text-tertiary">unknown customer</em>
        )}
      </div>
      {order.need_by_date ? (
        <div className="text-role-metadata">Need by {formatDate(order.need_by_date)}</div>
      ) : null}
      {order.status === 'blocked' && order.blocked_reason ? (
        <Chip size="xs" variant="danger" title={order.blocked_reason_detail ?? undefined}>
          {BLOCKED_REASON_LABELS[order.blocked_reason]}
        </Chip>
      ) : null}
      {order.status === 'partially_shipped' && order.lines_total != null ? (
        <Chip size="xs" variant="warning">
          {order.lines_shipped ?? 0} of {order.lines_total} items shipped
        </Chip>
      ) : null}
      {order.extraction_state !== 'complete' ? (
        <Chip size="xs" variant={order.extraction_state === 'failed' ? 'danger' : 'neutral'}>
          {extractionLabel(order.extraction_state)}
        </Chip>
      ) : null}
      {order.tracking_number ? (
        <div className="text-role-metadata">{order.tracking_number}</div>
      ) : null}
    </div>
  )
}

function extractionLabel(state: SampleOrderSummary['extraction_state']): string {
  if (state === 'pending') return 'Extraction queued'
  if (state === 'running') return 'Extracting…'
  if (state === 'failed') return 'Extraction failed'
  return ''
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
